import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth } from "../firebaseAuth";
import { db } from "../firebase";

const AuthContext = createContext({
  user: null,
  profile: null,
  role: null,
  claimsReady: false,
  profileReady: false,
  loading: true,
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState(null);
  const [claimsReady, setClaimsReady] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (current) => {
      setUser(current);
      if (!current) {
        setProfile(null);
        setRole(null);
        setClaimsReady(true);
        setProfileReady(true);
        setLoading(false);
        return;
      }
      setProfileReady(false);
      try {
        const token = await current.getIdTokenResult(true);
        setRole(token.claims?.role || null);
      } catch (err) {
        setRole(null);
      }
      setClaimsReady(true);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileReady(true);
      return undefined;
    }
    const unsub = onSnapshot(doc(db, "users", user.uid), (snap) => {
      setProfile(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      setProfileReady(true);
    }, () => {
      setProfile(null);
      setProfileReady(true);
    });
    return () => unsub();
  }, [user]);

  const value = useMemo(() => ({
    user,
    profile,
    role,
    claimsReady,
    profileReady,
    loading,
    logout: () => signOut(auth),
  }), [user, profile, role, claimsReady, profileReady, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
